import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import { useImageUpload } from '@/src/presentation/hooks/useImageUpload';
import { SearchUsersUseCase } from '@/src/domain/usecases/SearchUsersUseCase';
import {
  acceptTeamInvitationUseCase,
  cancelTeamInvitationUseCase,
  deleteTeamUseCase,
  getTeamByIdUseCase,
  getTeamInvitationsUseCase,
  getTeamMembersUseCase,
  leaveTeamUseCase,
  removeTeamMemberUseCase,
  sendTeamInvitationUseCase,
  updateTeamUseCase,
} from '@/src/core/dependencies';
import { ModerationError, Team, TeamInvitation, TeamMember } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { TeamDetailScreen } from '@/src/presentation/screens/TeamDetailScreen';
import { logger } from '@/src/core/utils';
import { platformAlert } from '@/src/core/utils/platformAlert';
import { useFocusEffect } from '@react-navigation/native';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import React, { useCallback, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';

const searchUsersUseCase = new SearchUsersUseCase();

export default function TeamDetailContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { id } = useLocalSearchParams<{ id: string }>();

  const [team, setTeam] = useState<Team | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<TeamInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Edit state (kept in sync with team on load)
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editIsPublic, setEditIsPublic] = useState(false);

  const isCaptain = !!team && team.userId === user?.id;
  const isCurrentMember = members.some((m) => m.userId === user?.id) || isCaptain;

  const { pickAndUploadImage: pickLogo, uploading: uploadingLogo } = useImageUpload({
    bucket: 'team-logos',
    userId: user?.id || '',
    preset: 'avatar',
    fieldType: 'avatar_image',
    onSuccess: handleUpdateLogo,
  });

  async function handleUpdateLogo(url: string) {
    if (!id || !user) return;
    try {
      setUpdating(true);
      const updated = await updateTeamUseCase.execute(id, { logoUrl: url }, user.id);
      setTeam(updated);
    } catch (error) {
      logger.error('TeamDetail', 'Failed to update logo', { error });
    } finally {
      setUpdating(false);
    }
  }

  useFocusEffect(
    useCallback(() => {
      loadTeamData();
    }, [id]),
  );

  const loadTeamData = async () => {
    if (!id || !user) return;
    try {
      setLoading(true);
      const [teamData, membersData, invitationsData] = await Promise.all([
        getTeamByIdUseCase.execute(id),
        getTeamMembersUseCase.execute(id),
        getTeamInvitationsUseCase.execute(user.id).catch(() => ({ received: [], sent: [] })),
      ]);

      if (!teamData) {
        platformAlert(t('common.error'), t('errors.notFound'));
        router.back();
        return;
      }

      setTeam(teamData);
      setMembers(membersData);
      setPendingInvitations(
        invitationsData.sent.filter((i) => i.teamId === id && i.status === 'pending'),
      );
      setEditName(teamData.name);
      setEditDescription(teamData.description || '');
      setEditIsPublic(teamData.isPublic ?? false);
    } catch (error) {
      logger.error('TeamDetail', 'Failed to load team', { error });
    } finally {
      setLoading(false);
    }
  };

  const handleInviteMember = async (email: string) => {
    if (!user || !team) return;
    try {
      setInviting(true);
      // Find user by email
      const results = await searchUsersUseCase.execute(email);
      const found = results.find((r) => r.email === email);
      if (!found) {
        platformAlert(t('common.error'), t('errors.userNotFound'));
        return;
      }

      await sendTeamInvitationUseCase.execute({
        teamId: team.id,
        inviterId: user.id,
        inviteeId: found.id,
        teamName: team.name,
        inviterName: user.name || user.email || '',
      });

      platformAlert(t('common.success'), t('teams.invitationSent'));
      // Refresh invitations list to show the new pending invitation
      await loadTeamData();
    } catch (error) {
      logger.error('TeamDetail', 'Failed to invite member', { error });
      platformAlert(t('common.error'), error instanceof Error ? error.message : t('errors.genericError'));
    } finally {
      setInviting(false);
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      await cancelTeamInvitationUseCase.execute(invitationId);
      setPendingInvitations((prev) => prev.filter((i) => i.id !== invitationId));
    } catch (error) {
      logger.error('TeamDetail', 'Failed to cancel invitation', { error });
      platformAlert(t('common.error'), error instanceof Error ? error.message : t('errors.genericError'));
    }
  };

  const handleRemoveMember = (memberUserId: string) => {
    if (!user || !team) return;
    platformAlert(
      t('teams.removeMember'),
      t('teams.confirmRemove'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: async () => {
            try {
              await removeTeamMemberUseCase.execute(team.id, user.id, memberUserId);
              setMembers((prev) => prev.filter((m) => m.userId !== memberUserId));
            } catch (error) {
              logger.error('TeamDetail', 'Failed to remove member', { error });
              platformAlert(t('common.error'), error instanceof Error ? error.message : t('errors.genericError'));
            }
          },
        },
      ],
    );
  };

  const handleLeaveTeam = () => {
    if (!user || !team) return;
    platformAlert(
      t('teams.leaveTeam'),
      t('teams.confirmLeave'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('teams.leaveTeam'),
          style: 'destructive',
          onPress: async () => {
            try {
              await leaveTeamUseCase.execute(team.id, user.id, team.userId);
              router.back();
            } catch (error) {
              logger.error('TeamDetail', 'Failed to leave team', { error });
              platformAlert(t('common.error'), error instanceof Error ? error.message : t('errors.genericError'));
            }
          },
        },
      ],
    );
  };

  const handleDeleteTeam = () => {
    if (!team) return;
    platformAlert(
      t('teams.deleteTeam'),
      t('teams.confirmDelete'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteTeamUseCase.execute(team.id);
              router.replace('/teams');
            } catch (error) {
              logger.error('TeamDetail', 'Failed to delete team', { error });
              platformAlert(t('common.error'), error instanceof Error ? error.message : t('errors.genericError'));
            }
          },
        },
      ],
    );
  };

  const handleSaveSettings = async () => {
    if (!user || !team) return;
    try {
      setUpdating(true);
      const updated = await updateTeamUseCase.execute(
        team.id,
        { name: editName, description: editDescription || undefined, isPublic: editIsPublic },
        user.id,
      );
      setTeam(updated);
      platformAlert(t('common.success'), t('common.saved'));
    } catch (error) {
      if (error instanceof ModerationError) {
        platformAlert(t('common.error'), t('moderation.contentBlocked'));
      } else {
        logger.error('TeamDetail', 'Failed to update team', { error });
        platformAlert(t('common.error'), error instanceof Error ? error.message : t('errors.genericError'));
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleSearchUser = async (query: string) => {
    const results = await searchUsersUseCase.execute(query);
    return results.map((r) => ({ id: r.id, email: r.email, name: r.name }));
  };

  if (loading || !team) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.background, alignItems: 'center', justifyContent: 'center' }}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <>
      <Stack.Screen options={{ title: team.name }} />
      <TeamDetailScreen
        team={team}
        members={members}
        isCaptain={isCaptain}
        isCurrentMember={isCurrentMember}
        currentUserId={user?.id || ''}
        inviting={inviting}
        updating={updating}
        uploadingLogo={uploadingLogo}
        editName={editName}
        editDescription={editDescription}
        editIsPublic={editIsPublic}
        onEditNameChange={setEditName}
        onEditDescriptionChange={setEditDescription}
        onEditIsPublicChange={setEditIsPublic}
        pendingInvitations={pendingInvitations}
        onInviteMember={handleInviteMember}
        onCancelInvitation={handleCancelInvitation}
        onRemoveMember={handleRemoveMember}
        onLeaveTeam={handleLeaveTeam}
        onDeleteTeam={handleDeleteTeam}
        onSaveSettings={handleSaveSettings}
        onUploadLogo={pickLogo}
        onNavigateToProfile={(userId) => router.push(`/profile/${userId}`)}
        onSearchUser={handleSearchUser}
        colors={colors}
      />
    </>
  );
}
